<script setup lang="ts">
  import { StockItemsService } from '@/api/services/stock-items.service';
  import { StockMovementsService } from '@/api/services/stock-movements.service';
  import RestaurantTierChip from '@/components/partials/RestaurantTierChip.vue';
  import { useUi } from '@/composition/use-ui';
  import { StockItemType } from '@/enums/StockItemType';
  import {
    STOCK_MOVEMENT_TYPE_LABELS,
    StockMovementType,
    USABLE_STOCK_MOVEMENT_TYPES,
  } from '@/enums/StockMovementType';
  import { IDataOptions } from '@/interfaces/IDataOptions';
  import { IStockItem } from '@/interfaces/IStockItem';
  import { useRestaurantStore } from '@/stores/restaurant.store';
  import { reactive, ref, watch } from 'vue';

  const restaurantStore = useRestaurantStore();
  const ui = useUi();

  const stockMovements = ref<any[] | null>(null);
  const selectedType = ref<StockItemType | null>(null);
  const selectedMovementType = ref<StockMovementType | null>(null);

  const paginationData = ref<IDataOptions>({
    page: 1,
    itemsPerPage: 10,
    mustSort: true,
    sortBy: ['createdAt'],
    sortDesc: [true],
  });

  const totalItems = ref(0);

  async function loadStockMovements() {
    if (!restaurantStore.restaurant.id || !restaurantStore.selectedBranch.id)
      return;

    try {
      const response = await StockMovementsService.getAll({
        restaurantId: restaurantStore.restaurant.id,
        branchId: restaurantStore.selectedBranch.id,
        itemType: selectedType.value,
        type: selectedMovementType.value,
        options: paginationData.value,
      });

      stockMovements.value = response.items;
      totalItems.value = response.total;
    } catch (error) {
      ui.serverError(error, 'Error al cargar los movimientos de stock');
    }
  }

  watch(
    [
      () => restaurantStore.restaurant.id,
      () => restaurantStore.selectedBranch.id,
      selectedType,
      selectedMovementType,
      paginationData,
    ],
    () => {
      loadStockMovements();
    },
    { immediate: true }
  );

  const typeTabs = [
    {
      title: 'Todo',
      value: null,
    },
    {
      title: 'Ingredientes',
      value: StockItemType.Ingredient,
    },
    {
      title: 'Productos',
      value: StockItemType.Product,
    },
  ];

  const movementTypeOptions = [
    { text: 'Todos', value: null },
    ...USABLE_STOCK_MOVEMENT_TYPES.filter(
      (type) => !!STOCK_MOVEMENT_TYPE_LABELS[type]
    )
    .map((type) => ({
      text: STOCK_MOVEMENT_TYPE_LABELS[type].label,
      value: type,
    })),
  ];

  const headers = [
    { text: 'Tipo', value: 'type', sortable: false },
    { text: 'Item', value: 'stockItem.name', sortable: false },
    //{ text: 'Cambio', value: 'direction', sortable: false },
    { text: 'Cantidad', value: 'quantity', sortable: false },
    { text: 'Nota', value: 'note', sortable: false },
    { text: 'Fecha', value: 'createdAt', sortable: false },
  ];

  function formatDate(date: string) {
    return new Date(date).toLocaleString();
  }
</script>

<template>
  <div class="stock-page">
    <div class="stock-page--inner">
      <v-card class="stock-page--card">
        <v-card-title>
          <div class="header-container">
            <div class="header-title-container">
              <v-icon left>mdi-history</v-icon>
              <span>Movimientos de stock</span>
              <RestaurantTierChip :tier="1" />
              <v-chip label color="amber" x-small>BETA</v-chip>
            </div>
          </div>
        </v-card-title>
        <v-card-text>
          <div class="stock-page--card-content">
            <div class="type-tabs">
              <v-btn
                v-for="tab in typeTabs"
                :key="tab.value"
                depressed
                small
                @click="selectedType = tab.value"
                :color="selectedType === tab.value ? 'primary' : 'default'"
                >{{ tab.title }}</v-btn
              >
            </div>
            <div class="tw-max-w-xs">
              <v-select
                v-model="selectedMovementType"
                :items="movementTypeOptions"
                label="Tipo de movimiento"
                dense
                outlined
                class="tw-mt-4 tw-mb-4"
                hide-details
              ></v-select>
            </div>
            <div class="stock-movements">
              <template v-if="!stockMovements">
                <v-skeleton-loader type="table-thead" />
              </template>
              <template v-else-if="stockMovements.length === 0">
                <p class="tw-text-center tw-text-gray-500">
                  No hay movimientos de stock registrados.
                </p>
              </template>
              <template v-else>
                <v-data-table
                  dense
                  :items="stockMovements"
                  :headers="headers"
                  :options.sync="paginationData"
                  :footer-props="{ 'items-per-page-options': [5, 10, 20, 50] }"
                  :server-items-length="totalItems"
                >
                  <!-- <template v-slot:item.direction="{ item }">
                    <v-chip
                      small
                      :color="item.direction === 1 ? 'success' : 'error'"
                      outlined
                    >
                      <v-icon left small>{{
                        item.direction === 1 ? 'mdi-plus' : 'mdi-minus'
                      }}</v-icon>
                      <span>{{
                        item.direction === 1 ? 'Entrada' : 'Salida'
                      }}</span>
                    </v-chip>
                  </template> -->
                  <template v-slot:item.type="{ item }">
                    <v-chip small>
                      <v-icon left small>{{
                        STOCK_MOVEMENT_TYPE_LABELS[item.type].icon
                      }}</v-icon>
                      {{ STOCK_MOVEMENT_TYPE_LABELS[item.type].label }}
                    </v-chip>
                  </template>
                  <template v-slot:item.quantity="{ item }">
                    <span
                      class="quantity"
                      :class="{
                        [`quantity--${
                          item.direction === 1 ? 'in' : 'out'
                        }`]: true,
                      }"
                    >
                      {{ item.direction === 1 ? '+' : '-' }}
                      {{ Math.abs(item.quantity) }}
                      {{ item.stockItem.unit }}
                    </span>
                  </template>
                  <template v-slot:item.createdAt="{ item }">
                    <div class="date-container">
                      <span class="date">{{ formatDate(item.createdAt) }}</span>
                      <timeago class="time-ago" :datetime="item.createdAt" />
                    </div>
                  </template>
                </v-data-table>
              </template>
            </div>
          </div>
        </v-card-text>
      </v-card>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .stock-page {
    @apply tw-flex tw-justify-center tw-items-center tw-h-full tw-py-4;

    &--inner {
      @apply tw-w-full tw-max-w-7xl;

      .stock-page--card {
        @apply tw-shadow-none tw-rounded-xl;

        .header-container {
          @apply tw-flex tw-justify-between tw-items-center tw-w-full;

          .header-title-container {
            @apply tw-flex tw-items-center tw-space-x-1.5;
          }
        }

        .stock-page--card-content {
          @apply tw-space-y-4;

          .type-tabs {
            @apply tw-flex tw-space-x-2;
          }

          .quantity {
            &--in {
              @apply tw-text-green-700;
            }

            &--out {
              @apply tw-text-red-700;
            }
          }

          .date-container {
            @apply tw-flex tw-flex-col tw-py-0.5;

            .date {
              @apply tw-text-xs;
            }

            .time-ago {
              @apply tw-text-gray-500 tw-text-xs;
              line-height: 1em;
            }
          }
        }
      }
    }
  }
</style>
